import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
          //bg-blue-500/80 backdrop-blur-sm text-white hover:bg-blue-600/90 border border-blue-400/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
        default: "bg-white/20 backdrop-blur-sm text-foreground hover:bg-white/30 border border-white/20 shadow-lg transition-all duration-300",
        destructive:
          "bg-red-600 backdrop-blur-sm text-white hover:bg-red-700/90 border border-red-500/30 shadow-lg transition-all duration-300",
        outline:
          "border border-white/30 bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:text-foreground shadow-lg transition-all duration-300",
        secondary:
          "bg-white/10 backdrop-blur-sm text-foreground hover:bg-white/20 shadow-lg border border-white/20 transition-all duration-300",
        ghost: "hover:bg-white/10 backdrop-blur-sm hover:text-foreground shadow-lg transition-all duration-300",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-lg px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    const glassStyle = {
      boxShadow: variant === 'destructive' 
        ? '0 4px 16px rgba(220,38,38,0.2), inset 0 1px 0 rgba(255,255,255,0.3)'
        : variant === 'ghost' || variant === 'link'
        ? undefined
        : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
      ...style
    };
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={glassStyle}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
